import Phaser from 'phaser';
import { Backdrop } from '../render/Backdrop';
import { Sfx } from '../audio/sfx';
import { drawNodeMark } from '../render/nodeMark';
import { drawWatermark } from '../render/watermark';
import { nodeStyle } from '../render/palette';
import { t } from '../i18n';
import { SANS, UI, uiButton, clean, reducedMotion } from '../render/theme';
import type { NodeType } from '../sim/types';

/**
 * NØDUS title screen.
 *
 * Layout (top → bottom):
 *   1. Top-bracket frame chrome
 *   2. Eyebrow chip ("CASCADE PROTOCOL // BUILD 0.1")
 *   3. Massive wordmark with chromatic aberration + glitch
 *   4. Japanese subtitle line
 *   5. Tagline + signature underline
 *   6. CALL-TO-ACTION chip with corner brackets + breathing glow
 *   7. Demo circuit running behind everything
 *   8. Bottom chrome: version + region + watermark
 */
export class BootScene extends Phaser.Scene {
  constructor() {
    super('Boot');
  }

  create() {
    new Backdrop(this, { glow: 0.25, scanlines: false, particles: false });
    const { width, height } = this.scale;

    // The network idles behind everything so the title already shows the game.
    this.spawnDemoCircuit();

    const titleY = height * 0.40;
    const title = this.add.text(width / 2, titleY, 'NØDUS', {
      fontFamily: SANS, fontSize: '170px', color: UI.text, fontStyle: '800',
    }).setOrigin(0.5);
    title.setShadow(0, 0, '#6a8cff', 40, true, true);

    this.add.text(width / 2, titleY + 130, clean(t('boot_tagline')), {
      fontFamily: SANS, fontSize: '32px', color: UI.textDim, fontStyle: '600',
      align: 'center', wordWrap: { width: width - 160 },
    }).setOrigin(0.5);

    const btnY = height * 0.62;
    const btn = uiButton(this, width / 2, btnY, 560, 132, clean(t('boot_cta')), UI.cool, () => {}, { primary: true, size: 38 });
    if (!reducedMotion()) {
      this.tweens.add({ targets: btn, scale: { from: 1, to: 1.04 }, duration: 1100, yoyo: true, repeat: -1, ease: 'Sine.easeInOut' });
    }

    drawWatermark(this);

    this.input.once('pointerdown', () => {
      const sfx = (this.registry.get('sfx') as Sfx | undefined) ?? new Sfx();
      this.registry.set('sfx', sfx);
      sfx.init();
      sfx.unlock();
      this.cameras.main.flash(160, 30, 60, 120);
      this.time.delayedCall(170, () => this.scene.start('Menu'));
    });
  }

  /**
   * Background constellation: dim nodes wired into a small network, with a
   * pulse hopping continuously. Sells the cascade fantasy at idle.
   */
  private spawnDemoCircuit() {
    const { width, height } = this.scale;

    const nodes: { id: string; x: number; y: number; type: NodeType }[] = [
      { id: 'n0', x: width * 0.16, y: height * 0.16, type: 'normal' },
      { id: 'n1', x: width * 0.84, y: height * 0.18, type: 'relay' },
      { id: 'n2', x: width * 0.12, y: height * 0.82, type: 'shield' },
      { id: 'n3', x: width * 0.86, y: height * 0.86, type: 'splitter' },
      { id: 'n4', x: width * 0.22, y: height * 0.52, type: 'required' },
      { id: 'n5', x: width * 0.78, y: height * 0.52, type: 'core' },
      { id: 'n6', x: width * 0.50, y: height * 0.88, type: 'capacitor' },
      { id: 'n7', x: width * 0.50, y: height * 0.12, type: 'protected' },
    ];
    const wires: [string, string][] = [
      ['n0', 'n4'], ['n4', 'n2'], ['n2', 'n6'],
      ['n6', 'n3'], ['n3', 'n5'], ['n5', 'n1'], ['n1', 'n7'],
      ['n7', 'n0'], ['n4', 'n5'], ['n0', 'n6'],
    ];
    const byId = new Map(nodes.map((n) => [n.id, n]));

    const wireG = this.add.graphics().setDepth(-60).setAlpha(0.55);
    wireG.lineStyle(1, 0x4a6cff, 0.30);
    for (const [a, b] of wires) {
      const A = byId.get(a)!;
      const B = byId.get(b)!;
      wireG.lineBetween(A.x, A.y, B.x, B.y);
    }

    for (const n of nodes) {
      const style = nodeStyle(n.type);
      const grp = this.add.container(n.x, n.y).setDepth(-58).setAlpha(0.55);
      const aura = this.add.circle(0, 0, 33, style.ring, 0.10).setBlendMode(Phaser.BlendModes.ADD);
      const bg = this.add.circle(0, 0, 21, style.fill);
      const ring = this.add.circle(0, 0, 21, undefined, 0).setStrokeStyle(2, style.ring, 0.85);
      const mark = this.add.graphics();
      drawNodeMark(mark, n.type, 21);
      grp.add([aura, bg, ring, mark]);

      this.tweens.add({
        targets: grp, alpha: { from: 0.55, to: 0.95 },
        duration: 1500 + Math.random() * 800,
        yoyo: true, repeat: -1, ease: 'Sine.easeInOut',
      });
    }

    const order = ['n0', 'n4', 'n5', 'n3', 'n6', 'n2', 'n0', 'n4', 'n7', 'n1', 'n5'];
    let stage = 0;
    const stepMs = 600;
    const playPulse = () => {
      const A = byId.get(order[stage % order.length])!;
      const B = byId.get(order[(stage + 1) % order.length])!;
      const dot = this.add.circle(A.x, A.y, 4.5, 0x88e8ff, 1)
        .setBlendMode(Phaser.BlendModes.ADD)
        .setDepth(-57);
      const halo = this.add.circle(A.x, A.y, 12, 0x88e8ff, 0.45)
        .setBlendMode(Phaser.BlendModes.ADD)
        .setDepth(-57);
      this.tweens.add({
        targets: [dot, halo], x: B.x, y: B.y,
        duration: stepMs, ease: 'Sine.easeInOut',
        onComplete: () => { dot.destroy(); halo.destroy(); },
      });
      stage++;
    };
    this.time.addEvent({ delay: stepMs, loop: true, callback: playPulse });
  }
}
